import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout';
import { getErrorMessage } from '@/services/api';
import { messagingService, type ChannelStatus } from '@/services/messaging';
import { formatDateTime } from '@/utils';

/** Country Admin: every broker's messaging channel status. Read-only, no secrets. */

const LABELS: Record<ChannelStatus, string> = {
  not_set: 'Not set',
  connected: 'Connected',
  failing: 'Failing',
  not_active: 'Not active yet',
};

function when(value: string | null): string {
  return value ? formatDateTime(value) : '—';
}

export function MessagingChannelsOverviewPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['messagingChannelOverview'],
    queryFn: messagingService.getOverview,
  });
  return (
    <div className="space-y-6">
      <PageHeader
        title="Messaging Channels"
        subtitle="Each broker's Telegram bot and WhatsApp set-up. Brokers manage their own bots; this view is read-only."
      />
      {isLoading && <p role="status" className="text-sm text-gray-500">Loading…</p>}
      {isError && (
        <p role="alert" className="text-sm text-red-600">Could not load channel status. {getErrorMessage(error)}</p>
      )}
      {data && data.length === 0 && <p className="text-sm text-gray-600">No brokers yet.</p>}
      {data && data.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-2">Broker</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Telegram</th>
                <th className="px-4 py-2">Bot</th>
                <th className="px-4 py-2">Last delivery</th>
                <th className="px-4 py-2">Last error</th>
                <th className="px-4 py-2">WhatsApp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {data.map((row) => (
                <tr key={row.broker_id}>
                  <td className="px-4 py-2">{row.broker_name}</td>
                  <td className="px-4 py-2">{row.broker_code}</td>
                  <td className="px-4 py-2">{LABELS[row.telegram_status]}</td>
                  <td className="px-4 py-2">{row.telegram_bot_username ? `@${row.telegram_bot_username}` : '—'}</td>
                  <td className="px-4 py-2">{when(row.telegram_last_delivery_at)}</td>
                  <td className="px-4 py-2">{when(row.telegram_last_error_at)}</td>
                  <td className="px-4 py-2">
                    {LABELS[row.whatsapp_status]}
                    {row.whatsapp_configured ? ' (details saved)' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MessagingChannelsOverviewPage;
